import React, { useState, useEffect } from 'react';
import { agentAPI, performanceAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import { FiTrendingUp, FiTarget, FiAward, FiCoffee, FiUsers, FiStar } from 'react-icons/fi';

const PerformanceCard = ({ title, target, achieved, coachingCount, isTeam }) => {
  const achievement = target > 0 ? (achieved / target) * 100 : 0;
  const formatCurrency = (value) => new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(value);

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${isTeam ? 'border-l-4 border-indigo-500' : ''}`}>
      <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-4">
        <div className="flex items-center">
          <SafeIcon icon={FiTarget} className="w-6 h-6 text-blue-600" />
          <div className="ml-3">
            <p className="text-sm text-gray-500">เป้าหมาย</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(target)}</p>
          </div>
        </div>
        <div className="flex items-center">
          <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600" />
          <div className="ml-3">
            <p className="text-sm text-gray-500">ทำได้จริง</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(achieved)}</p>
          </div>
        </div>
        <div className="flex items-center">
          <SafeIcon icon={FiAward} className="w-6 h-6 text-purple-600" />
          <div className="ml-3">
            <p className="text-sm text-gray-500">ความสำเร็จ</p>
            <p className="text-xl font-bold text-gray-900">{achievement.toFixed(2)}%</p>
          </div>
        </div>
        {coachingCount !== undefined && (
          <div className="flex items-center">
            <SafeIcon icon={FiCoffee} className="w-6 h-6 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm text-gray-500">จำนวนการโค้ช</p>
              <p className="text-xl font-bold text-gray-900">{coachingCount}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TopPerformerCard = ({ performer, rank }) => {
  const formatCurrency = (value) => new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(value);
  
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center">
        <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full font-bold text-white ${rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-orange-600' : 'bg-blue-500'}`}>
          {rank}
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-900">{performer.name}</p>
          <p className="text-xs text-gray-500">ความสำเร็จ {performer.achievement.toFixed(1)}%</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-gray-900">{formatCurrency(performer.sales)}</p>
      </div>
    </div>
  );
};

const LinePerformance = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: agent } = await agentAPI.getMyProfile();
        if (agent) {
          const { data } = await performanceAPI.getPerformanceByAgent(agent.id);
          setPerformanceData(data);
        } else {
           setError('ไม่พบข้อมูลโปรไฟล์ตัวแทน');
        }
      } catch (error) {
        console.error('Error fetching performance data:', error);
        setError('เกิดข้อผิดพลาดในการโหลดข้อมูลผลงาน');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  if (!performanceData) {
    return <div className="p-8 text-center text-gray-500">ไม่สามารถโหลดข้อมูลผลงานได้</div>;
  }

  const { personal, team, topPerformers } = performanceData;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">ผลงาน</h1>
        <p className="text-gray-600">ติดตามยอดขายและความสำเร็จของคุณ</p>
      </div>

      <div className="space-y-8">
        {/* Personal Section */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiTarget} className="mr-2"/> ผลงานส่วนตัว</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <PerformanceCard title="ภาพรวมรายปี" {...personal.year} />
            <PerformanceCard title="ผลงานไตรมาส 1" {...personal.q1} />
            <PerformanceCard title="ผลงานไตรมาส 2" {...personal.q2} />
            <PerformanceCard title="ผลงานไตรมาส 3" {...personal.q3} />
            <PerformanceCard title="ผลงานไตรมาส 4" {...personal.q4} />
          </div>
        </div>

        {/* Team Section (if applicable) */}
        {team && team.memberCount > 1 && (
          <div>
             <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center"><SafeIcon icon={FiUsers} className="mr-2"/> ผลงานทีม</h2>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PerformanceCard 
                  title="ยอดขายทีมรวม" 
                  target={team.target} 
                  achieved={team.totalSales} 
                  isTeam={true}
                />
                
                {/* Top Performers */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <SafeIcon icon={FiStar} className="mr-2 text-yellow-500"/> 
                    อันดับยอดเยี่ยมสูงสุด
                  </h3>
                  <div className="space-y-3">
                    {topPerformers && topPerformers.length > 0 ? (
                      topPerformers.map((performer, index) => (
                        <TopPerformerCard key={performer.id} performer={performer} rank={index + 1} />
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">ยังไม่มีข้อมูลผลงานของลูกทีม</p>
                    )}
                  </div>
                </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinePerformance;
